import { arrayWithoutSubset, updateRecord } from '@ms/yammer-data-support';
import { StorylineAnnouncementAudience, StorylineAnnouncementNotificationTarget } from '@ms/yammer-graphql';

import {
  PublisherAnnouncementAudienceAction,
  PublisherAnnouncementDeliverySelectedAction,
  PublisherAnnouncementNotificationTargetsAction,
  PublisherGroupChangedAction,
  PublisherMandatoryNotificationSelectedAction,
  PublisherNotifiedUsersAddedAction,
  PublisherNotifiedUsersChangedAction,
  PublisherNotifiedUsersRemovedAction,
  PublisherOnBehalfOfSenderChangedAction,
  PublisherTargetChangedFromGroupToUserAction,
  PublisherTargetChangedFromUserToGroupAction,
} from '../../domains/publishers/actions.types';
import { Publishers, isUserThreadStarterPublisher } from '../types';

import { isPublisherFileAttachmentReference } from './attachments.types';
import { PublishersAction } from './reducer.types';
import {
  GroupThreadStarterPublisher,
  Publisher,
  PublisherPostType,
  UserThreadStarterPublisher,
  isGroupThreadStarterPublisher,
  isUserPublisher,
} from './types';

const addNotifiedUsersToPublisher = (state: Publishers, payload: PublisherNotifiedUsersAddedAction['payload']) => {
  const { id, userIds } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher) {
    return state;
  }

  const notifiedUserIds = new Set([...previousPublisher.notifiedUserIds, ...userIds]);
  const updatedPublisherFields = {
    notifiedUserIds: Array.from(notifiedUserIds),
  };

  return updateRecord(state, id, updatedPublisherFields);
};

const removeNotifiedUsersFromPublisher = (
  state: Publishers,
  { id, userIds }: PublisherNotifiedUsersRemovedAction['payload']
) => {
  const previousPublisher = state[id];
  if (!previousPublisher) {
    return state;
  }

  const updatedPublisherFields = {
    notifiedUserIds: arrayWithoutSubset(previousPublisher.notifiedUserIds, userIds),
  };

  return updateRecord(state, id, updatedPublisherFields);
};

const changeNotifiedUsersFromPublisher = (
  state: Publishers,
  { id, userIds }: PublisherNotifiedUsersChangedAction['payload']
) => updateRecord(state, id, { notifiedUserIds: userIds });

const destinationChangedWhileFilesAttached = (attachments: Publisher['attachments']) => {
  const initialAttachments = attachments;
  const attachmentsWithoutUploads = initialAttachments.filter((attachment) => {
    if (isPublisherFileAttachmentReference(attachment)) {
      return !attachment.wasAttachedViaUpload;
    }

    return true;
  });
  const filesWereRemoved = attachmentsWithoutUploads.length < initialAttachments.length;

  return {
    attachmentsWithoutUploads,
    filesWereRemoved,
  };
};

const changePublisherGroup = (
  state: Publishers,
  { id, groupId, onBehalfOfSenderId }: PublisherGroupChangedAction['payload']
) => {
  if (state[id] == null) {
    return state;
  }

  const { attachmentsWithoutUploads, filesWereRemoved } = destinationChangedWhileFilesAttached(state[id].attachments);

  const updatedPublisherFields = {
    groupId,
    changedGroupWhileFilesAttached: filesWereRemoved,
    attachments: attachmentsWithoutUploads,
    onBehalfOfSenderId,
    isAnnouncement: false,
  };

  return updateRecord(state, id, updatedPublisherFields);
};

const groupPostTypesToUserPostTypes: Record<
  GroupThreadStarterPublisher['postType'],
  UserThreadStarterPublisher['postType']
> = {
  [PublisherPostType.GroupNewUpdate]: PublisherPostType.UserNewUpdate,
  [PublisherPostType.GroupNewPoll]: PublisherPostType.UserNewPoll,
  [PublisherPostType.GroupNewPraise]: PublisherPostType.UserNewPraise,
  [PublisherPostType.GroupNewQuestion]: PublisherPostType.UserNewQuestion,
  [PublisherPostType.GroupNewArticle]: PublisherPostType.UserNewArticle,
};

const changePublisherTargetFromGroupToUser = (
  state: Publishers,
  { id, userId }: PublisherTargetChangedFromGroupToUserAction['payload']
) => {
  const oldPublisher = state[id];
  if (!isGroupThreadStarterPublisher(oldPublisher)) {
    return state;
  }

  const { attachmentsWithoutUploads, filesWereRemoved } = destinationChangedWhileFilesAttached(state[id].attachments);

  const correspondingUserPostType = groupPostTypesToUserPostTypes[oldPublisher.postType];

  const updatedPublisherFields: Partial<UserThreadStarterPublisher> = {
    postType: correspondingUserPostType,
    userId,
    changedGroupWhileFilesAttached: filesWereRemoved,
    attachments: attachmentsWithoutUploads,
  };

  return updateRecord(state, id, updatedPublisherFields);
};

const userPostTypesToGroupPostTypes: Record<
  UserThreadStarterPublisher['postType'],
  GroupThreadStarterPublisher['postType']
> = {
  [PublisherPostType.UserNewUpdate]: PublisherPostType.GroupNewUpdate,
  [PublisherPostType.UserNewPoll]: PublisherPostType.GroupNewPoll,
  [PublisherPostType.UserNewPraise]: PublisherPostType.GroupNewPraise,
  [PublisherPostType.UserNewQuestion]: PublisherPostType.GroupNewQuestion,
  [PublisherPostType.UserNewArticle]: PublisherPostType.GroupNewArticle,
  [PublisherPostType.UserNewMedia]: PublisherPostType.GroupNewUpdate,
};

const changePublisherTargetFromUserToGroup = (
  state: Publishers,
  { id }: PublisherTargetChangedFromUserToGroupAction['payload']
) => {
  const oldPublisher = state[id];
  if (!isUserThreadStarterPublisher(oldPublisher)) {
    return state;
  }

  const { attachmentsWithoutUploads, filesWereRemoved } = destinationChangedWhileFilesAttached(state[id].attachments);

  const correspondingGroupPostType = userPostTypesToGroupPostTypes[oldPublisher.postType];

  const updatedPublisherFields = {
    postType: correspondingGroupPostType,
    groupId: null,
    userId: undefined,
    changedGroupWhileFilesAttached: filesWereRemoved,
    attachments: attachmentsWithoutUploads,
  };

  return updateRecord(state, id, updatedPublisherFields);
};

const updateOnBehalfOfSenderId = (state: Publishers, payload: PublisherOnBehalfOfSenderChangedAction['payload']) => {
  const { publisherId, onBehalfOfSenderId } = payload;

  let updatedPublisherFields: Partial<UserThreadStarterPublisher> = {
    onBehalfOfSenderId,
  };

  const publisher = state[publisherId];
  if (publisher && isUserPublisher(publisher)) {
    const { attachmentsWithoutUploads, filesWereRemoved } = destinationChangedWhileFilesAttached(publisher.attachments);

    updatedPublisherFields = {
      ...updatedPublisherFields,
      changedPostOnBehalfOfUserWhileFilesAttached: filesWereRemoved,
      attachments: attachmentsWithoutUploads,
      isAnnouncement: false,
    };
  }

  return updateRecord(state, publisherId, updatedPublisherFields);
};

type SetAnnouncement = (props: {
  readonly state: Publishers;
  readonly id: Publisher['id'];
  readonly isAnnouncement: boolean;
  readonly isEditingDraftGroupThreadStarter?: boolean;
  readonly isEditingDraftUserThreadStarter?: boolean;
  readonly announcementAudienceType?: StorylineAnnouncementAudience;
  readonly announcementAudienceId?: string[];
}) => Publishers;
const setAnnouncement: SetAnnouncement = ({
  state,
  id,
  isAnnouncement,
  isEditingDraftGroupThreadStarter,
  isEditingDraftUserThreadStarter,
  announcementAudienceType,
  announcementAudienceId,
}) => {
  const publisher = state[id];

  if (isGroupThreadStarterPublisher(publisher) || isEditingDraftGroupThreadStarter) {
    const updatedPublisherFields: Partial<GroupThreadStarterPublisher> = {
      isAnnouncement,
    };

    return updateRecord(state, id, updatedPublisherFields);
  }

  if (isUserThreadStarterPublisher(publisher) || isEditingDraftUserThreadStarter) {
    const updatedPublisherFields: Partial<UserThreadStarterPublisher> = {
      isAnnouncement,
      announcementAudience: announcementAudienceType,
      announcementAudienceIds: announcementAudienceId,
      announcementNotificationTargets: isAnnouncement
        ? [StorylineAnnouncementNotificationTarget.EMAIL, StorylineAnnouncementNotificationTarget.TEAMS]
        : undefined,
    };

    return updateRecord(state, id, updatedPublisherFields);
  }

  return state;
};

type ToggleMandatoryNotificationChecked = (options: {
  readonly state: Publishers;
  readonly id: Publisher['id'];
  readonly isMandatoryNotification: boolean;
  readonly isEditingDraftGroupThreadStarter: boolean;
}) => Publishers;

const toggleMandatoryNotificationChecked: ToggleMandatoryNotificationChecked = ({
  state,
  id,
  isMandatoryNotification,
  isEditingDraftGroupThreadStarter,
}) => {
  const publisher = state[id];

  if (isGroupThreadStarterPublisher(publisher) || isEditingDraftGroupThreadStarter) {
    const updatedPublisherFields: Partial<GroupThreadStarterPublisher> = { isMandatoryNotification };

    return updateRecord(state, id, updatedPublisherFields);
  }

  return state;
};

const announcementDeliverySelected = (
  state: Publishers,
  {
    id,
    isEditingDraftGroupThreadStarter,
    isEditingDraftUserThreadStarter,
    announcementAudienceType,
    announcementAudienceId,
  }: PublisherAnnouncementDeliverySelectedAction['payload']
) =>
  setAnnouncement({
    state,
    id,
    isAnnouncement: true,
    isEditingDraftGroupThreadStarter,
    isEditingDraftUserThreadStarter,
    announcementAudienceType,
    announcementAudienceId,
  });

const standardDeliverySelected = (
  state: Publishers,
  {
    id,
    isEditingDraftGroupThreadStarter,
    isEditingDraftUserThreadStarter,
  }: PublisherAnnouncementDeliverySelectedAction['payload']
) =>
  setAnnouncement({
    state,
    id,
    isAnnouncement: false,
    isEditingDraftGroupThreadStarter,
    isEditingDraftUserThreadStarter,
  });

const mandatoryNotificationSelected = (
  state: Publishers,
  {
    id,
    isMandatoryNotification,
    isEditingDraftGroupThreadStarter,
  }: PublisherMandatoryNotificationSelectedAction['payload']
) => toggleMandatoryNotificationChecked({ state, id, isMandatoryNotification, isEditingDraftGroupThreadStarter });

const toggleMentionsBar = (state: Publishers, publisherId: Publisher['id']) => {
  const publisher = state[publisherId];

  const updatedPublisherFields = {
    hasToggledMentionsBarToVisible: !publisher.hasToggledMentionsBarToVisible,
  };

  return updateRecord(state, publisherId, updatedPublisherFields);
};

const updateAnnouncementAudienceValue = (
  state: Publishers,
  payload: PublisherAnnouncementAudienceAction['payload']
) => {
  const { id: publisherId, announcementAudience, announcementAudienceIds } = payload;

  return updateRecord(state, publisherId, { announcementAudience, announcementAudienceIds });
};

const updateAnnouncementNotificationTargets = (
  state: Publishers,
  payload: PublisherAnnouncementNotificationTargetsAction['payload']
) => {
  const { id: publisherId, announcementNotificationTargets } = payload;

  return updateRecord(state, publisherId, { announcementNotificationTargets });
};

type AudienceActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const audienceActionsReducer: AudienceActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.TOGGLE_TEAMS_MEETING_ANONYMOUS_MESSAGE':
      return updateRecord(state, action.payload.publisherId, { isAnonymousMessage: action.payload.isAnonymousMessage });
    case 'PUBLISHER.NOTIFIED_USERS_ADDED':
      return addNotifiedUsersToPublisher(state, action.payload);
    case 'PUBLISHER.NOTIFIED_USERS_REMOVED':
      return removeNotifiedUsersFromPublisher(state, action.payload);
    case 'PUBLISHER.NOTIFIED_USERS_CHANGED':
      return changeNotifiedUsersFromPublisher(state, action.payload);
    case 'PUBLISHER.GROUP_CHANGED':
      return changePublisherGroup(state, action.payload);
    case 'PUBLISHER.TARGET_CHANGED_FROM_GROUP_TO_USER':
      return changePublisherTargetFromGroupToUser(state, action.payload);
    case 'PUBLISHER.TARGET_CHANGED_FROM_USER_TO_GROUP':
      return changePublisherTargetFromUserToGroup(state, action.payload);
    case 'PUBLISHER.ON_BEHALF_OF_SENDER_CHANGED':
      return updateOnBehalfOfSenderId(state, action.payload);
    case 'PUBLISHER.ANNOUNCEMENT_DELIVERY_SELECTED':
      return announcementDeliverySelected(state, action.payload);
    case 'PUBLISHER.STANDARD_DELIVERY_SELECTED':
      return standardDeliverySelected(state, action.payload);
    case 'PUBLISHER.MANDATORY_NOTIFICATION_SELECTED':
      return mandatoryNotificationSelected(state, action.payload);
    case 'PUBLISHER.MENTIONS_BAR_TOGGLED':
      return toggleMentionsBar(state, action.payload.publisherId);
    case 'PUBLISHER.ANNOUNCEMENT_AUDIENCE_CHANGED':
      return updateAnnouncementAudienceValue(state, action.payload);
    case 'PUBLISHER.ANNOUNCEMENT_NOTIFICATION_TARGETS_CHANGED':
      return updateAnnouncementNotificationTargets(state, action.payload);

    default:
      return state;
  }
};
